import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queries } from '../../common/api/queryKeys/queries';
import { useDebounce } from '../../common/hooks/useDebounce';
import { toDemandDefinitionRequestInput } from '../toDemandDefinitionRequestInput';
import type { DemandDefinitionState } from '../types/DemandDefinitionState';
import { FleetChangeHandling } from '../types/FleetChangeHandling';

const CALCULATION_DEBOUNCE_MS = 500;
const CALCULATION_STALE_TIME_MS = 30_000;

export const useDemandCalculation = (
    demandDefinition: DemandDefinitionState,
    enabled: boolean,
    customerGroupId?: string,
    definitionId?: string,
    ignoreLeavingVessels?: boolean
) => {
    const queryClient = useQueryClient();
    const {
        adjustments,
        fleetChanges,
        leavingVessels: stateLeavingVessels,
        excludedCompanyIds,
        excludedProductCategories,
        excludedProductKeys,
        referencePeriodFrom,
        referencePeriodTo,
    } = demandDefinition;
    const leavingVessels = ignoreLeavingVessels ? undefined : stateLeavingVessels;
    const input = useMemo(
        () =>
            toDemandDefinitionRequestInput({
                adjustments,
                leavingVessels: leavingVessels ?? [],
                fleetChanges: fleetChanges.filter(
                    (change) => change.handling !== FleetChangeHandling.MIRROR_VESSEL || Boolean(change.mirrorVesselId)
                ),
                excludedCompanyIds,
                excludedProductCategories,
                excludedProductKeys,
                referencePeriodFrom,
                referencePeriodTo,
            }),
        [
            adjustments,
            fleetChanges,
            leavingVessels,
            excludedCompanyIds,
            excludedProductCategories,
            excludedProductKeys,
            referencePeriodFrom,
            referencePeriodTo,
        ]
    );
    const debouncedInput = useDebounce(input, CALCULATION_DEBOUNCE_MS);
    const isDebouncing = debouncedInput !== input;
    const queryOptions = queries.demandDefinitions.calculation(input, customerGroupId, definitionId);
    const query = useQuery({
        ...queryOptions,
        enabled: enabled && !isDebouncing,
        staleTime: CALCULATION_STALE_TIME_MS,
        placeholderData: keepPreviousData,
    });
    const hasCurrentData = query.data !== undefined && !query.isPlaceholderData;
    const cachedQueryState = queryClient.getQueryState(queryOptions.queryKey);
    const hasFreshCachedData =
        hasCurrentData &&
        cachedQueryState !== undefined &&
        !cachedQueryState.isInvalidated &&
        cachedQueryState.dataUpdatedAt + CALCULATION_STALE_TIME_MS > Date.now() + CALCULATION_DEBOUNCE_MS;

    return {
        ...query,
        input,
        data: hasCurrentData ? query.data : undefined,
        retainedData: query.data,
        isFetching: enabled && (query.isFetching || (isDebouncing && !hasFreshCachedData)),
    };
};
